import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowRight01Icon,
  CloudServerIcon,
  CodeIcon,
  Database01Icon,
  Rocket01Icon,
  SecurityCheckIcon,
  SparklesIcon,
  WorkflowCircle06Icon,
  CheckmarkCircle02Icon,
  Alert02Icon,
  ShieldKeyIcon,
  FolderLibraryIcon,
  Layers01Icon,
  Globe02Icon,
  Building03Icon,
  ReactIcon,
  FlashIcon,
  PaintBrush02Icon,
  CheckListIcon,
  TextFontIcon,
  SourceCodeIcon,
  GitlabIcon,
} from '@hugeicons/core-free-icons';
import { Link, useNavigate } from 'react-router';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { OperationalPageHeader, operationalCardClass } from '@/components/shared/OperationalUI';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

// ── Índice de secciones (navegación lateral tipo "en esta página") ──
const SECTIONS = [
  { id: 'en-una-frase', label: '1. En una frase' },
  { id: 'donde-vive', label: '2. ¿Dónde vivirá tu app?' },
  { id: 'tecnologias', label: '3. Tecnologías recomendadas' },
  { id: 'repositorios', label: '4. Repositorios base' },
  { id: 'servicios', label: '5. Qué te entrega VIA' },
  { id: 'ambientes', label: '6. Los ambientes' },
  { id: 'ruta', label: '7. La ruta paso a paso' },
  { id: 'seguridad', label: '8. Seguridad mínima' },
  { id: 'brain', label: '9. Brain: conocimiento' },
  { id: 'compromiso', label: '10. Nuestro compromiso' },
];

// ── Repositorios base para iniciar el desarrollo ──
// Se clonan por SSH desde el GitLab corporativo (puerto 2222). El acceso se
// habilita tras el registro y la evaluación de la iniciativa.
const BASE_REPOS = [
  {
    icon: SourceCodeIcon,
    name: 'Repositorio base · Lógica del producto',
    tech: 'Go · variantes on-premise y cloud',
    desc: 'Servicio ya configurado con la estructura, convenciones y conexiones listas. Partes de aquí para escribir tus reglas de negocio.',
    href: 'https://gitlab.ucchristus.ai/via-apps/backend-base',
  },
  {
    icon: ReactIcon,
    name: 'Repositorio base · Experiencia (UX/UI)',
    tech: 'React 19 + Vite + Tailwind + shadcn',
    desc: 'Base de interfaz con la identidad visual UC CHRISTUS, componentes y temas claro/oscuro ya integrados.',
    href: 'https://gitlab.ucchristus.ai/via-apps/frontend-base',
  },
  {
    icon: FolderLibraryIcon,
    name: 'Brain · Conocimiento y harness',
    tech: 'Convenciones, patrones y políticas',
    desc: 'El repositorio de conocimiento para construir: arquitectura, base de datos, seguridad, ambientes y despliegues.',
    href: 'https://gitlab.ucchristus.ai/via-apps/ia-brain-pri-op-gl',
  },
];

// ── Accesos destacados (repos, registro y recursos) ──
// NOTA: completar las URLs externas reales (GitLab corporativo) donde se indica.
const QUICK_ACCESS = [
  {
    kind: 'anchor',
    href: '#repositorios',
    label: 'Repositorios base',
    desc: 'Backend, experiencia (UX/UI) y Brain, listos para clonar.',
    icon: SourceCodeIcon,
  },
  {
    kind: 'internal',
    to: '/demanda/nueva',
    label: 'Registrar tu iniciativa',
    desc: 'Da de alta tu producto para iniciar el registro y la evaluación.',
    icon: Rocket01Icon,
  },
  {
    kind: 'internal',
    to: '/kb',
    label: 'Recursos y Knowledge Hub',
    desc: 'Guías, prompts y buenas prácticas para los equipos.',
    icon: SparklesIcon,
  },
  {
    kind: 'internal',
    to: '/brand',
    label: 'Identidad UC CHRISTUS',
    desc: 'Logos, colores, tipografía y activos de marca.',
    icon: PaintBrush02Icon,
  },
];

// ── Stack de experiencia de usuario (UX/UI) con icono conceptual Hugeicons ──
// Nota: usamos iconografía Hugeicons (estándar de marca), no logos de terceros.
const FRONTEND_STACK = [
  { label: 'React 19 + TypeScript', icon: ReactIcon },
  { label: 'Vite', icon: FlashIcon },
  { label: 'Tailwind CSS v4 + shadcn/ui', icon: PaintBrush02Icon },
  { label: 'Zustand', icon: Layers01Icon },
  { label: 'TanStack Query', icon: Database01Icon },
  { label: 'React Hook Form + Zod', icon: CheckListIcon },
  { label: 'Hugeicons (Stroke Rounded)', icon: SparklesIcon },
  { label: 'Inter', icon: TextFontIcon },
];

// ── Secciones de contenido reutilizables ──
function SectionHeading({ id, icon, eyebrow, title }) {
  return (
    <div id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <HugeiconsIcon icon={icon} className="size-5" aria-hidden="true" />
        </div>
        <div>
          {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{eyebrow}</p>}
          <h2 className="font-display text-xl font-extrabold leading-tight text-foreground md:text-2xl">{title}</h2>
        </div>
      </div>
    </div>
  );
}

function Callout({ tone = 'info', children }) {
  const tones = {
    info: 'border-secondary/40 bg-secondary/5',
    warning: 'border-amber-500/40 bg-amber-500/5',
  };
  const icon = tone === 'warning' ? Alert02Icon : SecurityCheckIcon;
  const iconColor = tone === 'warning' ? 'text-amber-500' : 'text-secondary';
  return (
    <div className={cn('flex gap-3 rounded-xl border p-4 text-sm leading-relaxed', tones[tone])}>
      <HugeiconsIcon icon={icon} className={cn('mt-0.5 size-5 shrink-0', iconColor)} aria-hidden="true" />
      <div className="text-foreground/90">{children}</div>
    </div>
  );
}

export default function DeveloperGuide() {
  const navigate = useNavigate();

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-10">
      <OperationalPageHeader
        eyebrow="Plataforma VIA · Valor de la IA"
        title="Guía de Desarrollo sobre la Plataforma VIA"
        subtitle="Para equipos de UC CHRISTUS que quieran construir aplicaciones y productos digitales. Tu equipo se concentra en la lógica de negocio; la plataforma aporta la infraestructura, los servicios y las buenas prácticas."
        actions={
          <Button onClick={() => navigate('/demanda/nueva')}>
            <HugeiconsIcon icon={Rocket01Icon} data-icon="inline-start" />
            Registrar iniciativa
          </Button>
        }
      />

      {/* ── Franja de accesos rápidos ── */}
      <StaggerContainer className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.04}>
        {QUICK_ACCESS.map((item) => {
          const inner = (
            <Card className={cn(operationalCardClass, 'group h-full cursor-pointer')}>
              <CardContent className="flex h-full flex-col gap-3 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <HugeiconsIcon icon={item.icon} className="size-5" aria-hidden="true" />
                  </div>
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{item.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                </div>
              </CardContent>
            </Card>
          );
          return (
            <StaggerItem key={item.label}>
              {item.kind === 'internal' ? (
                <Link
                  to={item.to}
                  className="block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  {inner}
                </Link>
              ) : item.kind === 'anchor' ? (
                <a
                  href={item.href}
                  className="block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  {inner}
                </a>
              ) : (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${item.label} (se abre en una pestaña nueva)`}
                  className="block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  {inner}
                </a>
              )}
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      {/* ── Layout de dos columnas: índice + contenido ── */}
      <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
        {/* Índice "en esta página" */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              En esta página
            </p>
            <nav aria-label="Secciones de la guía" className="flex flex-col gap-1 border-l border-border">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="-ml-px border-l-2 border-transparent py-1.5 pl-3 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
                  {s.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Contenido */}
        <div className="flex min-w-0 flex-col gap-12">
          {/* 1. En una frase */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading id="en-una-frase" icon={SparklesIcon} eyebrow="En una frase" title="En una frase" />
            <div className="rounded-2xl bg-gradient-to-br from-primary to-secondary p-6 text-white md:p-8">
              <p className="font-display text-xl font-extrabold leading-snug md:text-2xl">
                Tu equipo se concentra en el producto; la plataforma aporta los recursos y servicios.
              </p>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              VIA funciona como una plataforma interna de desarrollo —al estilo de las plataformas modernas que entregan
              todo listo para construir, pero corporativa—: tu equipo dispone de ambientes, base de datos, envío de
              correo, autenticación, almacenamiento de archivos y despliegue. El equipo de TI opera la capa de
              infraestructura, de modo que tu equipo no tiene que mantenerla.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <Card className={operationalCardClass}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Lo que aporta tu equipo</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <ul className="flex flex-col gap-2">
                    <li className="flex gap-2">
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="mt-0.5 size-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <span>
                        La <strong className="text-foreground">lógica de tu producto</strong> (tus reglas de negocio y
                        cómo funciona).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="mt-0.5 size-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <span>
                        La <strong className="text-foreground">experiencia de usuario (UX/UI)</strong> (las pantallas y
                        cómo se usa tu producto).
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card className={operationalCardClass}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Lo que aporta la plataforma</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <ul className="flex flex-col gap-2">
                    <li className="flex gap-2">
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="mt-0.5 size-4 shrink-0 text-secondary"
                        aria-hidden="true"
                      />
                      <span>
                        Un <strong className="text-foreground">punto de partida común</strong> para backend y frontend
                        (repositorios base).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="mt-0.5 size-4 shrink-0 text-secondary"
                        aria-hidden="true"
                      />
                      <span>
                        Las <strong className="text-foreground">conexiones por ambiente</strong> entregadas como un
                        archivo <code className="rounded bg-muted px-1 py-0.5 text-xs">.env</code>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="mt-0.5 size-4 shrink-0 text-secondary"
                        aria-hidden="true"
                      />
                      <span>
                        El <strong className="text-foreground">despliegue</strong> y la{' '}
                        <strong className="text-foreground">operación</strong> de la infraestructura.
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </MotionSection>

          {/* 2. Dónde vivirá */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="donde-vive"
              icon={Layers01Icon}
              eyebrow="Primera decisión"
              title="¿Dónde vivirá tu aplicación?"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Antes de escribir una línea de código, define el destino. Esto cambia los requisitos.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <Card className={operationalCardClass}>
                <CardHeader>
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <HugeiconsIcon icon={Building03Icon} className="size-5" aria-hidden="true" />
                  </div>
                  <CardTitle className="pt-2 text-base">On-premise (interna)</CardTitle>
                  <CardDescription>Personal interno, dentro de la red corporativa</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Dónde corre:</strong> servidores de UC CHRISTUS.
                  </p>
                  <p>
                    <strong className="text-foreground">Exposición:</strong> red interna / VPN.
                  </p>
                  <p>
                    <strong className="text-foreground">Controles de borde:</strong> los básicos de la plataforma.
                  </p>
                  <Badge variant="secondary" className="w-fit gap-1">
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-3" aria-hidden="true" />
                    Ethical hacking no requerido
                  </Badge>
                </CardContent>
              </Card>
              <Card className={operationalCardClass}>
                <CardHeader>
                  <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <HugeiconsIcon icon={Globe02Icon} className="size-5" aria-hidden="true" />
                  </div>
                  <CardTitle className="pt-2 text-base">Pública (expuesta a internet)</CardTitle>
                  <CardDescription>Pacientes o público general, desde internet</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Dónde corre:</strong> AWS (Lambda), gestionado por TI.
                  </p>
                  <p>
                    <strong className="text-foreground">Exposición:</strong> dominio público (
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">*.ucchristus.ai</code>).
                  </p>
                  <p>
                    <strong className="text-foreground">Controles de borde:</strong> WAF, rate limiting, hardening
                    reforzado.
                  </p>
                  <Badge className="w-fit gap-1">
                    <HugeiconsIcon icon={Alert02Icon} className="size-3" aria-hidden="true" />
                    Ethical hacking obligatorio
                  </Badge>
                </CardContent>
              </Card>
            </div>
            <Callout tone="warning">
              <strong>Regla clave:</strong> si tu aplicación se expone a internet, es obligatorio pasar por un ethical
              hacking (prueba de seguridad ofensiva) antes de publicarla. Si es solo on-premise para uso interno, ese
              paso no aplica, aunque las buenas prácticas de seguridad siguen siendo obligatorias en ambos casos.
            </Callout>
          </MotionSection>

          {/* 3. Tecnologías */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading id="tecnologias" icon={CodeIcon} eyebrow="Stack común" title="Tecnologías recomendadas" />
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="mb-2 font-semibold text-foreground">La lógica de tu producto</h3>
                <div className="overflow-hidden rounded-xl border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lenguaje</TableHead>
                        <TableHead>Cuándo usarlo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">
                          Go 1.25+{' '}
                          <Badge variant="secondary" className="ml-1">
                            preferido
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          APIs y servicios. Opción por defecto: rápida y la que mejor se integra con la plataforma.
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">Node.js / TypeScript</TableCell>
                        <TableCell className="text-muted-foreground">
                          APIs ligeras, integraciones, automatizaciones.
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">Python 3.11+ / FastAPI</TableCell>
                        <TableCell className="text-muted-foreground">
                          Procesamiento de datos, IA, cargas intensivas.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-4">
                  <Callout tone="info">
                    <strong>Por qué Go por defecto:</strong> es el lenguaje que mejor se integra con nuestra plataforma
                    de despliegue y el que operamos con mayor eficiencia. Escribes tu servicio en Go y la plataforma se
                    encarga del empaquetado y la puesta en marcha.
                  </Callout>
                </div>
              </div>
              <div>
                <h3 className="mb-2 font-semibold text-foreground">La experiencia de usuario (UX/UI)</h3>
                <div className="flex flex-wrap gap-2">
                  {FRONTEND_STACK.map((t) => (
                    <span
                      key={t.label}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground">
                      <HugeiconsIcon icon={t.icon} className="size-3.5 text-primary" aria-hidden="true" />
                      {t.label}
                    </span>
                  ))}
                </div>
                <div className="mt-4">
                  <Callout tone="info">
                    Toda interfaz debe respetar la identidad visual UC CHRISTUS (primario{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">#671E75</code>, secundario{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">#307FE2</code>, modo claro/oscuro). Los
                    logos, colores y fuentes se consumen desde una URL central —{' '}
                    <Link to="/brand" className="font-medium text-secondary hover:underline">
                      Identidad UC CHRISTUS
                    </Link>
                    — para que un cambio de marca se refleje en todos lados.
                  </Callout>
                </div>
              </div>
            </div>
          </MotionSection>

          {/* 4. Repositorios base */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="repositorios"
              icon={SourceCodeIcon}
              eyebrow="Punto de partida"
              title="Repositorios base"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Partes de repositorios ya configurados y publicados en el GitLab corporativo. Así llegas directo a
              construir tu producto, no a montar infraestructura. Abre en GitLab el que necesites y clónalo desde ahí.
            </p>
            <StaggerContainer className="grid gap-4 md:grid-cols-3" stagger={0.04}>
              {BASE_REPOS.map((repo) => (
                <StaggerItem key={repo.name}>
                  <Card className={cn(operationalCardClass, 'flex h-full flex-col')}>
                    <CardHeader className="pb-3">
                      <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <HugeiconsIcon icon={repo.icon} className="size-5" aria-hidden="true" />
                      </div>
                      <CardTitle className="pt-3 text-base">{repo.name}</CardTitle>
                      <CardDescription>{repo.tech}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-1 flex-col justify-between gap-4">
                      <p className="text-sm leading-relaxed text-muted-foreground">{repo.desc}</p>
                      <Button variant="outline" size="sm" className="w-full" asChild>
                        <a
                          href={repo.href}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Abrir ${repo.name} en GitLab (se abre en una pestaña nueva)`}>
                          <HugeiconsIcon icon={GitlabIcon} data-icon="inline-start" />
                          Ver repositorio en GitLab
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </StaggerContainer>
            <Callout tone="info">
              El acceso a los repositorios se habilita tras el registro y la evaluación de tu iniciativa, y requiere tu
              llave SSH cargada en el GitLab corporativo. Si aún no registraste tu iniciativa,{' '}
              <Link to="/demanda/nueva" className="font-medium text-secondary hover:underline">
                regístrala aquí
              </Link>
              .
            </Callout>
          </MotionSection>

          {/* 5. Servicios */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="servicios"
              icon={CloudServerIcon}
              eyebrow="Tu proveedor interno"
              title="Qué te entrega VIA"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Para cada producto y cada ambiente que necesites, VIA construye los servicios y te entrega un{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">.env</code> con las conexiones.
            </p>
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Necesitas…</TableHead>
                    <TableHead>VIA te da…</TableHead>
                    <TableHead>Cómo te conectas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    [
                      'Base de datos',
                      'PostgreSQL 16 (tabular y búsqueda vectorial) o DynamoDB (alto volumen)',
                      'Cadena de conexión en tu .env',
                    ],
                    ['Enviar correos', 'Correo transaccional (SES / SendGrid)', 'Credenciales en tu .env'],
                    [
                      'Guardar archivos',
                      'Bucket de almacenamiento (S3)',
                      'Credenciales y nombre del bucket en tu .env',
                    ],
                    ['Autenticación / login', 'Single Sign-On con Azure EntraID', 'Configuración en tu .env'],
                    ['Generar PDF', 'Conversión HTML a PDF (Gotenberg)', 'Endpoint en tu .env'],
                    ['Colas / tareas', 'Colas de mensajes (SQS)', 'Configuración en tu .env'],
                    [
                      'Secretos y credenciales',
                      'Almacén seguro central (SSM)',
                      'VIA los inyecta; nunca van en el repo',
                    ],
                  ].map(([a, b, c]) => (
                    <TableRow key={a}>
                      <TableCell className="font-medium text-foreground">{a}</TableCell>
                      <TableCell className="text-muted-foreground">{b}</TableCell>
                      <TableCell className="text-muted-foreground">{c}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-foreground">
                El <code className="rounded bg-muted px-1 py-0.5 text-sm">.env</code> por proyecto y por ambiente
              </h3>
              <pre className="overflow-x-auto rounded-xl bg-[#0F172A] p-4 text-xs leading-relaxed text-slate-200">
                {`# Ejemplo de lo que recibes (los valores reales los entrega VIA, nunca van al repo)
DATABASE_URL=...          # tu base de datos para este ambiente
MAIL_API_KEY=...          # servicio de correo
DOCUMENTS_BUCKET=...      # almacenamiento de archivos
AUTH_TENANT_ID=...        # autenticación SSO`}
              </pre>
            </div>
            <Callout tone="warning">
              <strong>Regla de seguridad:</strong> el <code className="rounded bg-muted px-1 py-0.5 text-xs">.env</code>{' '}
              con valores reales nunca se sube al repositorio. En el repo solo vive un{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">.env.example</code> con placeholders. Los valores
              reales viven en el almacén seguro y VIA te los entrega.
            </Callout>
          </MotionSection>

          {/* 6. Ambientes */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading id="ambientes" icon={Database01Icon} eyebrow="Copias aisladas" title="Los ambientes" />
            <StaggerContainer className="grid gap-4 md:grid-cols-3" stagger={0.04}>
              {[
                {
                  k: 'dev',
                  t: 'Desarrollo del día a día. Aquí pruebas tus cambios.',
                  tone: 'bg-primary/10 text-primary',
                },
                {
                  k: 'stg',
                  t: 'Pre-producción. Validación final, demos y pruebas de aceptación.',
                  tone: 'bg-secondary/10 text-secondary',
                },
                {
                  k: 'prd',
                  t: 'Producción. Lo que usan las personas reales.',
                  tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
                },
              ].map((env) => (
                <StaggerItem key={env.k}>
                  <Card className={cn(operationalCardClass, 'h-full')}>
                    <CardContent className="flex flex-col gap-2 p-5">
                      <span
                        className={cn('w-fit rounded-lg px-2.5 py-1 font-mono text-sm font-bold uppercase', env.tone)}>
                        {env.k}
                      </span>
                      <p className="text-sm text-muted-foreground">{env.t}</p>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </StaggerContainer>
            <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <HugeiconsIcon
                  icon={CheckmarkCircle02Icon}
                  className="mt-0.5 size-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span>
                  El <strong className="text-foreground">frontend corre en tu equipo</strong> (
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">localhost</code>) y consume las APIs del
                  ambiente que corresponda.
                </span>
              </li>
              <li className="flex gap-2">
                <HugeiconsIcon
                  icon={CheckmarkCircle02Icon}
                  className="mt-0.5 size-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span>
                  El <strong className="text-foreground">backend se despliega</strong> a cada ambiente mediante el flujo
                  que VIA te entrega.
                </span>
              </li>
              <li className="flex gap-2">
                <HugeiconsIcon
                  icon={CheckmarkCircle02Icon}
                  className="mt-0.5 size-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span>
                  No se crean bases de datos locales ni mocks: siempre trabajas contra servicios reales del ambiente.
                </span>
              </li>
            </ul>
          </MotionSection>

          {/* 7. Ruta paso a paso */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading id="ruta" icon={WorkflowCircle06Icon} eyebrow="Ruta clara" title="La ruta paso a paso" />
            <ol className="flex flex-col gap-4">
              {[
                [
                  'Define tu producto',
                  '¿On-premise o pública? ¿Qué ambientes (dev, stg, prd)? ¿Qué servicios (base de datos, correo, archivos, auth)?',
                ],
                [
                  'Registro y evaluación',
                  'Toda aplicación pasa por el registro de VIA. La asignación de base de datos y accesos ocurre una vez completada la evaluación.',
                ],
                [
                  'Recibe tus repositorios base',
                  'Partes de un repositorio base ya configurado en el GitLab corporativo: backend (on-premise y cloud) y frontend.',
                ],
                [
                  'Recibe tu .env por ambiente',
                  'VIA construye los servicios y te entrega las conexiones. Copias el .env, no tocas nada más de infraestructura.',
                ],
                [
                  'Desarrolla',
                  'Backend en Go (de preferencia) con tus reglas de negocio; frontend conectado a tu API. Pruebas en local contra dev.',
                ],
                [
                  'Valida',
                  'Antes de desplegar: build, pruebas y revisión. Tu código debe pasar los controles de calidad y seguridad básicos.',
                ],
                [
                  'Despliega',
                  'Implementar nunca significa desplegar automáticamente: un despliegue siempre requiere aprobación explícita. Si es pública, ethical hacking obligatorio antes de producción.',
                ],
              ].map(([title, desc], i) => (
                <li key={title} className="flex gap-4">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="pt-0.5">
                    <p className="font-semibold text-foreground">{title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </MotionSection>

          {/* 8. Seguridad */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="seguridad"
              icon={ShieldKeyIcon}
              eyebrow="Base común"
              title="Seguridad: lo mínimo que todos cumplen"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Estas reglas aplican a <strong className="text-foreground">todos</strong> los proyectos, sean on-premise o
              públicos.
            </p>
            <StaggerContainer className="grid gap-3 md:grid-cols-2" stagger={0.03}>
              {[
                'Cero secretos en el repositorio: credenciales, tokens y llaves van al almacén seguro.',
                '.env local es solo tuyo (no se sube). En el repo solo el .env.example con placeholders.',
                'Autenticación con la cuenta corporativa (Azure EntraID / SSO) en apps internas.',
                'Validación en el backend: el frontend valida por comodidad, pero la validación de verdad vive en el backend.',
                'Consultas parametrizadas siempre (nunca armar SQL concatenando texto).',
                'Identificadores UUID v7 en bases de datos; nunca exponer IDs secuenciales.',
                'Datos sensibles (PHI) solo en sessionStorage, nunca en localStorage.',
              ].map((rule, i) => (
                <StaggerItem key={i}>
                  <div className="flex h-full gap-3 rounded-xl border border-border p-4 text-sm">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      {i + 1}
                    </span>
                    <span className="text-muted-foreground">{rule}</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
            <Callout tone="info">
              <strong>On-premise:</strong> cumples las 7 reglas; no se exige ethical hacking. <strong>Pública:</strong>{' '}
              cumples las 7 reglas y además pasas por ethical hacking antes de producción, con controles de borde
              reforzados (WAF, rate limiting, hardening).
            </Callout>
          </MotionSection>

          {/* 9. Brain */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="brain"
              icon={FolderLibraryIcon}
              eyebrow="Conocimiento compartido"
              title="Brain: el conocimiento compartido"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              <code className="rounded bg-muted px-1 py-0.5 text-xs">brain</code> es nuestro repositorio de conocimiento
              para construir aplicaciones. Contiene las convenciones, patrones y políticas que usamos (arquitectura de
              backend, frontend, base de datos, seguridad, ambientes, despliegues). Antes de empezar algo, es la fuente
              a consultar: permite aprovechar las soluciones que ya funcionan, manteniendo un estándar de calidad
              unificado entre los equipos.
            </p>
          </MotionSection>

          {/* 10. Compromiso */}
          <MotionSection className="flex flex-col gap-5">
            <SectionHeading
              id="compromiso"
              icon={SparklesIcon}
              eyebrow="Nuestro compromiso"
              title="Nuestro compromiso"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              La intención es acompañar, no añadir trabas ni sobre-esfuerzo. Buscamos que cada equipo desarrolle su
              producto con agilidad y calidad, bajo tres premisas compartidas:
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  icon: SecurityCheckIcon,
                  t: 'Seguridad',
                  d: 'Que lo construido no exponga a la organización ni a sus datos.',
                },
                {
                  icon: Rocket01Icon,
                  t: 'Eficiencia',
                  d: 'Aprovechar la infraestructura ya resuelta en lugar de rehacerla.',
                },
                {
                  icon: CheckmarkCircle02Icon,
                  t: 'Buenas prácticas',
                  d: 'Que cualquier equipo pueda entender y mantener el resultado.',
                },
              ].map((c) => (
                <Card key={c.t} className={operationalCardClass}>
                  <CardContent className="flex flex-col gap-2 p-5">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <HugeiconsIcon icon={c.icon} className="size-5" aria-hidden="true" />
                    </div>
                    <p className="font-semibold text-foreground">{c.t}</p>
                    <p className="text-sm text-muted-foreground">{c.d}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </MotionSection>

          {/* ── CTA final ── */}
          <MotionSection>
            <div className="flex flex-col items-start gap-4 rounded-2xl bg-gradient-to-br from-primary to-secondary p-6 text-white md:flex-row md:items-center md:justify-between md:p-8">
              <div>
                <p className="font-display text-xl font-extrabold">¿Listo para empezar?</p>
                <p className="mt-1 text-sm text-white/90">
                  Registra tu iniciativa para iniciar el registro, la evaluación y recibir tus repositorios y
                  conexiones.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  className="bg-white text-primary hover:bg-white/90"
                  onClick={() => navigate('/demanda/nueva')}>
                  <HugeiconsIcon icon={Rocket01Icon} data-icon="inline-start" />
                  Registrar iniciativa
                </Button>
                <Button
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/kb')}>
                  <HugeiconsIcon icon={SparklesIcon} data-icon="inline-start" />
                  Explorar recursos
                </Button>
              </div>
            </div>
          </MotionSection>
        </div>
      </div>
    </div>
  );
}
